/**
 * Copyright 2023 AutoZone, Inc.
 * Content is confidential to and proprietary information of AutoZone, Inc., its
 * subsidiaries and affiliates.
 */

import { requestBaseURL, requestBaseURLGCPExtOrInt } from '@/config/serviceAPI';
import { useFeatureFlag } from '@/features/globalConfig';
import { getAxios } from '@/lib/axios';
import { createQuery } from '@/utils/createReactQuery';
import { type QueryClient } from '@tanstack/react-query';
import { type AxiosError, type AxiosInstance } from 'axios';
import type {
  ProductReviewsData,
  ProductReviewsOptions,
  ProductReviewsResponse,
  RatingDistribution,
} from '../interface';
import logger from '@/utils/logger';

const URL = `${requestBaseURL}/external/product-discovery/seo-data/v1/product-reviews`;
const GCP_URL = `${requestBaseURLGCPExtOrInt}/sls/b2c/product-discovery-seo-data-bs/v2/product-reviews`;

const getProductReviews = async (
  options: ProductReviewsOptions,
  axiosInstance?: AxiosInstance
): Promise<ProductReviewsData> => {
  const {
    country,
    customerType,
    skuId,
    filterByMediaType,
    filterByStar,
    filterBykeyword,
    pageNumber,
    reviewsWithMedia,
    sortBy,
    gcpUrlEnabled,
  } = options;

  const response = await getAxios(axiosInstance)
    .get<ProductReviewsResponse>(gcpUrlEnabled ? GCP_URL : URL, {
      params: {
        country,
        customerType,
        skuId,
        filterByMediaType,
        filterByStar: filterByStar?.join(','),
        filterBykeyword,
        pageNumber,
        reviewsWithMedia,
        sortBy,
      },
      ...(gcpUrlEnabled && { withCredentials: false }),
    })
    .catch((err: AxiosError) => {
      logger.error({
        message: 'Error fetching product reviews.',
        meta: {
          code: err.code,
          message: err.message,
          name: err.name,
          stack: err.stack,
          status: err.status,
          response: {
            ...err.response,
            request: undefined,
            config: undefined,
          },
          config: {
            ...err.config,
            httpsAgent: undefined,
          },
        },
      });

      throw err;
    });
  return selector(response.data);
};

const selector = (data: ProductReviewsResponse): ProductReviewsData => {
  const ratingsDistribution: RatingDistribution[] = [5, 4, 3, 2, 1].map((star) => {
    const currentDistribution = data.ratingsDistribution?.find(
      (distribution) => distribution.star === star
    );

    return currentDistribution ?? { star, count: 0, percentage: 0 };
  });

  return {
    ...data,
    ratingsDistribution,
  };
};

const {
  useData: useProductReviewsData,
  prefetch: prefetchProductReviewsData,
  query: productReviewsQuery,
} = createQuery('reviews', getProductReviews);

export const useProductReviews = <T = ProductReviewsData>(
  options: ProductReviewsOptions & {
    enabled?: boolean;
    keepPreviousData?: boolean;
    select?: (data: ProductReviewsData) => T;
  }
) => {
  const productReviewsEnabled = useFeatureFlag('PRP_ENABLED') === 'true';

  return useProductReviewsData({
    ...options,
    enabled: productReviewsEnabled && options.enabled,
  });
};

export const prefetchProductReviews = (
  options: ProductReviewsOptions,
  queryClient: QueryClient,
  axiosInstance: AxiosInstance
) => {
  return prefetchProductReviewsData(queryClient, options, axiosInstance);
};

export const getProductReviewsFromCache = (
  options: ProductReviewsOptions,
  queryClient: QueryClient
) => {
  return queryClient.getQueryData<ProductReviewsData>(productReviewsQuery.getFullKey(options));
};
